import { useState } from "react";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, Tooltip, IconButton, styled, Chip } from '@mui/material';
import "react-pro-sidebar/dist/css/styles.css";
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DashboardIcon from "@mui/icons-material/Dashboard";
import CampaignIcon from "@mui/icons-material/Campaign";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/Lock';
import { useSubscription } from '../../contexts/SubscriptionContext';

const Item = ({ title, to, icon, selected, isCollapsed, requiredModule, badge }) => {
  const { hasModule, getRecommendedPlan } = useSubscription();
  const hasAccess = !requiredModule || hasModule(requiredModule);
  const isLocked = requiredModule && !hasAccess;
  
  return (
    <Tooltip 
      title={isLocked ? `Plan ${getRecommendedPlan(requiredModule)} requis` : ''} 
      arrow 
      placement="right"
    >
      <MenuItem 
        active={selected} 
        icon={icon} 
        className={selected ? 'active-menu-item' : ''}
        disabled={isLocked}
        style={{ opacity: isLocked ? 0.5 : 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography sx={{ flex: 1 }}>{title}</Typography>
          {isLocked && !isCollapsed && (
            <LockIcon fontSize="small" sx={{ ml: 1, fontSize: 14, color: 'text.disabled' }} />
          )}
          {badge && !isLocked && !isCollapsed && (
            <Chip
              label={badge}
              size="small"
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                ml: 1,
                background: 'linear-gradient(135deg, #ab47bc 0%, #8e24aa 100%)',
                color: 'white'
              }}
            />
          )}
        </Box>
        {!isLocked && <Link to={to} />}
      </MenuItem>
    </Tooltip>
  );
};

const menuItems = [
  // { title: "Dashboard", to: "/dashboard", icon: <DashboardIcon />, requiredModule: "advanced_dashboards", badge: "Pro" }, // Hidden - not yet implemented
  { title: "Chat", to: "/chat", icon: <ChatOutlinedIcon /> }, // Accessible to all with quota limits
  { title: "Segment", to: "/segment", icon: <PeopleAltOutlinedIcon /> },
  { title: "Campagne", to: "/campagne", icon: <CampaignIcon /> },
  { title: "Contacts", to: "/contacts", icon: <PersonOutlineOutlinedIcon /> },
  {
    title: "Canal",
    icon: <WifiTetheringIcon />,
    children: [
      { title: "Emails", to: "/emails", icon: <EmailOutlinedIcon /> },
    ],
  },
];

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed' && prop !== 'isHovered',
})(({ theme, isCollapsed, isHovered }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  height: '100vh',
  zIndex: 1200,
  backgroundColor: '#FFFFFF',
  transition: 'width 0.3s ease-in-out',
  width: isCollapsed && !isHovered ? '80px' : '270px',
  borderRight: '1px solid #E0E0E0',
  display: 'flex',
  flexDirection: 'column',

  '.pro-sidebar-inner': {
    backgroundColor: 'transparent !important',
    paddingTop: '20px',
    flexGrow: 1,
  },
  '.pro-sidebar-layout': {
    overflowY: 'auto !important',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#c1c1c1',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#a8a8a8',
    },
  },
  '.pro-icon-wrapper': {
    backgroundColor: 'transparent !important',
    color: '#5F6368',
    fontSize: '20px',
  },
  '.pro-inner-item': {
    padding: '8px 20px !important',
    margin: '4px 10px',
    borderRadius: '8px',
    color: '#5F6368',
    fontWeight: 500,
    transition: 'color 0.2s, background-color 0.2s',
  },
  '.pro-inner-item:hover': {
    color: '#007BFF !important',
    backgroundColor: '#E9F5FF !important',
  },
  '.pro-menu-item.active > .pro-inner-item': {
    color: '#0056b3 !important',
    backgroundColor: '#D1E7FF !important',
  },
  '.pro-sidebar:not(.collapsed) .pro-inner-item > .pro-icon-wrapper': {
    marginRight: '10px',
  },
  '.pro-sidebar.collapsed .pro-inner-item': {
    justifyContent: 'center',
  },
  '.pro-sub-menu > .pro-inner-list-item': {
    backgroundColor: '#F5F7FA !important', // Le fond gris clair pour le conteneur
    margin: '8px 12px',
    borderRadius: '8px',
    '& ul': { // La liste à l'intérieur doit être transparente
      backgroundColor: 'transparent !important',
    },
    '& .pro-inner-item': { // Les items aussi, pour voir le fond du conteneur
      backgroundColor: 'transparent !important',
    },
  },
  '.pro-menu-item': {
    '&.active': {
      '.MuiTypography-root': {
        fontWeight: '600 !important',
      },
      '.pro-icon-wrapper': {
        color: '#0056b3 !important',
      }
    },
  },
  '.active-menu-item > .pro-inner-item, .active-menu-item > .pro-inner-item > .pro-icon-wrapper': {
    color: '#0056b3 !important',
    fontWeight: '600 !important',
  },
  '.MuiTypography-root': {
    fontFamily: `'Inter', 'Montserrat', sans-serif`,
    fontSize: '14px',
  },
  /*'.pro-sub-menu-content': {
      backgroundColor: 'transparent !important',
  }*/
}));

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const appVersion = (process.env.REACT_APP_VERSION || "").trim();

  const menuKey = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const visuallyCollapsed = isCollapsed && !isHovered;

  return (
    <SidebarWrapper
      isCollapsed={isCollapsed}
      isHovered={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ProSidebar collapsed={visuallyCollapsed} breakPoint="md">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: visuallyCollapsed ? 'center' : 'space-between',
            padding: !visuallyCollapsed ? '16px 20px' : '16px 12px',
            backgroundColor: 'transparent',
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {/* Logo + nom */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <a href="/dashboard">
              <Box
                component="img"
                src="/QILINSAAMPicon1.png"
                alt="Logo"
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '8px',
                }}
              />
            </a>
            {!visuallyCollapsed && (
              <Typography
                variant="h4"
                sx={{
                  color: '#212529',
                  fontWeight: 700,
                  marginLeft: 1.5,
                  fontSize: 25,
                  fontFamily: `'Inter', 'Montserrat', sans-serif`,
                }}
              >
                Qilinsa Amplify
              </Typography>
            )}
          </Box>

          {/* Bouton collapse/déplie */}
          {!visuallyCollapsed && (
            <Tooltip title={isCollapsed ? "Épingler" : "Détacher"} placement="right" arrow>
              <IconButton onClick={() => setIsCollapsed(!isCollapsed)} sx={{ color: '#5F6368' }}>
                {isCollapsed ? <LastPageIcon /> : <FirstPageIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Menu iconShape="circle">
          {menuItems.map((item, index) => {
            if (item.children) {
              const isSubMenuActive = item.children.some(child => menuKey(child.to));
              return (
                <SubMenu
                  key={index}
                  title={item.title}
                  icon={item.icon}
                  defaultOpen={isSubMenuActive}
                  className={isSubMenuActive ? 'active-menu-item' : ''}
                >
                  {item.children.map((child, childIndex) => (
                    <Item
                      key={childIndex}
                      title={child.title}
                      to={child.to}
                      icon={child.icon}
                      selected={menuKey(child.to)}
                      isCollapsed={visuallyCollapsed}
                      requiredModule={child.requiredModule}
                      badge={child.badge}
                    />
                  ))}
                </SubMenu>
              );
            } else {
              return (
                <Item
                  key={index}
                  title={item.title}
                  to={item.to}
                  icon={item.icon}
                  selected={menuKey(item.to)}
                  isCollapsed={visuallyCollapsed}
                  requiredModule={item.requiredModule}
                  badge={item.badge}
                />
              );
            }
          })}
        </Menu>
      </ProSidebar>
      {appVersion && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 12,
            fontSize: '11px',
            color: '#868e96',
            fontFamily: `'Inter', 'Montserrat', sans-serif`,
          }}
        >
          v{appVersion.replace(/^v/i, '')}
        </Box>
      )}
    </SidebarWrapper>
  );
};

export default Sidebar;
